import { useState } from 'react'
import { serversApi } from '../../api'
import type { ServerLogsParams } from '../../api'
import type { MachineLog, MachineLogType, ServerLogsPage } from '../../types'
import { LOG_TYPES, TYPE_LABELS, formatTime } from './shared'
import { MessageCard, TypeBadge } from './ui'

function LogTable({ logs, empty, showMachine }: { logs: MachineLog[]; empty: string; showMachine: boolean }) {
  const cols = showMachine ? 5 : 4
  return (
    <div className="bg-surface rounded-xl border border-gray-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500 text-xs">
          <tr>
            <th className="text-left px-4 py-3 w-48">시각</th>
            <th className="text-left px-4 py-3 w-28">종류</th>
            {showMachine && <th className="text-left px-4 py-3">설비</th>}
            <th className="text-left px-4 py-3">코드</th>
            <th className="text-left px-4 py-3">값</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {logs.length === 0 ? (
            <tr>
              <td colSpan={cols} className="px-4 py-8 text-center text-gray-400 text-sm">{empty}</td>
            </tr>
          ) : (
            logs.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 text-gray-500 text-xs font-mono whitespace-nowrap">{formatTime(log.collectedAt)}</td>
                <td className="px-4 py-2.5"><TypeBadge type={log.type} /></td>
                {showMachine && <td className="px-4 py-2.5 font-medium text-gray-800">{log.machineCode}</td>}
                <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{log.code}</td>
                <td className="px-4 py-2.5 text-gray-700">{log.value}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}

interface QueryForm {
  from: string
  to: string
  type: '' | MachineLogType
  machineCode: string
  code: string
}

// machineCode 를 주면 그 설비로 한정하고 설비코드 입력·열을 숨긴다 (마스터데이터에서 설비를 고른 경우).
// 설비가 바뀌면 부모가 key 로 다시 마운트해 이전 결과를 비운다.
export default function QueryView({ serverId, machineCode }: { serverId: string; machineCode?: string }) {
  const [form, setForm] = useState<QueryForm>({ from: '', to: '', type: '', machineCode: '', code: '' })
  const [applied, setApplied] = useState<QueryForm | null>(null)
  const [pages, setPages] = useState<MachineLog[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const buildParams = (f: QueryForm, nextCursor?: string): ServerLogsParams => {
    const params: ServerLogsParams = {}
    if (f.type) params.type = f.type
    const machine = machineCode ?? f.machineCode
    if (machine) params.machineCode = machine
    if (f.code) params.code = f.code
    if (f.from) params.from = new Date(f.from).toISOString()
    if (f.to) params.to = new Date(f.to).toISOString()
    if (nextCursor) params.cursor = nextCursor
    return params
  }

  const load = async (f: QueryForm, nextCursor?: string) => {
    setLoading(true)
    setError(false)
    try {
      const { data } = await serversApi.getServerLogs(serverId, buildParams(f, nextCursor))
      const page = data as ServerLogsPage
      setPages(prev => (nextCursor ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const onSearch = () => {
    setApplied(form)
    setPages([])
    setCursor(null)
    load(form)
  }

  const onMore = () => {
    if (applied && cursor) load(applied, cursor)
  }

  return (
    <div>
      <div className="bg-surface rounded-xl border border-gray-200 p-4 mb-4">
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${machineCode ? 'lg:grid-cols-4' : 'lg:grid-cols-5'}`}>
          <div>
            <label className="block text-xs text-gray-500 mb-1">시작</label>
            <input type="datetime-local" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">종료</label>
            <input type="datetime-local" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">종류</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as QueryForm['type'] })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">전체</option>
              {LOG_TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>
          {!machineCode && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">설비코드</label>
              <input value={form.machineCode} onChange={e => setForm({ ...form, machineCode: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-500 mb-1">코드</label>
            <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={onSearch} disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover transition disabled:opacity-50">
            조회
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-500 mb-3">로그를 불러올 수 없습니다.</div>}

      {applied ? (
        <>
          <LogTable logs={pages} empty="조회 결과가 없습니다." showMachine={!machineCode} />
          <div className="flex items-center justify-center mt-4">
            {cursor ? (
              <button onClick={onMore} disabled={loading}
                className="border border-gray-300 text-gray-600 px-5 py-2 rounded-lg text-sm hover:bg-gray-50 transition disabled:opacity-50">
                {loading ? '불러오는 중…' : '더 보기'}
              </button>
            ) : pages.length > 0 ? (
              <span className="text-xs text-gray-400">마지막까지 조회했습니다 (총 {pages.length}건)</span>
            ) : null}
          </div>
        </>
      ) : (
        <MessageCard>조건을 설정하고 조회하세요.</MessageCard>
      )}
    </div>
  )
}
